import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiDownload,
  FiEye,
  FiPhoneCall,
  FiRefreshCw,
  FiUserCheck,
  FiXCircle,
} from "react-icons/fi";
import AssignCallModal from "../components/calls/AssignCallModal";
import RecordingButton from "../components/calls/RecordingButton";
import StartCallModal from "../components/calls/StartCallModal";
import Badge from "../components/common/Badge";
import Button from "../components/common/Button";
import EmptyState from "../components/common/EmptyState";
import RetryBlock from "../components/common/RetryBlock";
import SearchInput from "../components/common/SearchInput";
import Skeleton from "../components/common/Skeleton";
import TranscriptModal from "../components/modals/TranscriptModal";
import { useAuth } from "../context/AuthContext";
import useDebounce from "../hooks/useDebounce";
import useShellContext from "../layout/useShellContext";
import { getAiConfigs } from "../services/aiConfigApi";
import {
  assignCall,
  cancelCall,
  getAssignableCallingAgents,
  getCallDetail,
  getCallHistory,
  getCallTickets,
  getCalls,
  getCallStats,
  retryCall,
  updateCallTicketStatus,
} from "../services/callsApi";
import { startOutboundCall } from "../services/outboundApi";
import { syncRecordings as triggerRecordingSync } from "../services/recordingsApi";
import { downloadTranscript } from "../utils/callDisplay";
import { normalizeApiError } from "../utils/errors";
import {
  formatDateTime,
  formatDuration,
  formatNow,
  formatPhone,
} from "../utils/formatters";
import { isAdminUser } from "../utils/roleAccess";

const CALLS_TAB = "calls";
const HISTORY_TAB = "history";
const TICKETS_TAB = "tickets";
const PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 15000;

const CANCELLABLE_STATUSES = new Set([
  "pending",
  "queued",
  "scheduled",
  "retry_scheduled",
  "calling",
  "ringing",
  "answered",
  "initiated",
  "in_progress",
  "assigned",
  "callback_required",
  "follow_up_pending",
]);

const RETRYABLE_STATUSES = new Set([
  "failed",
  "dropped",
  "busy",
  "no_answer",
  "cancelled",
]);

const STATUS_LABELS = {
  pending: "Pending",
  queued: "Queued",
  scheduled: "Scheduled",
  retry_scheduled: "Scheduled",
  calling: "Live",
  ringing: "Live",
  answered: "Live",
  initiated: "In Progress",
  in_progress: "In Progress",
  assigned: "Pending",
  callback_required: "Pending",
  follow_up_pending: "Pending",
  completed: "Success",
  booked: "Success",
  success: "Success",
  escalated: "Escalated",
  human_needed: "Human Needed",
  failed: "Failed",
  dropped: "Dropped",
  busy: "Dropped",
  no_answer: "Dropped",
  cancelled: "Cancelled",
  not_interested: "Failed",
};

const STATUS_VARIANTS = {
  pending: "info",
  queued: "info",
  scheduled: "info",
  retry_scheduled: "info",
  calling: "warning",
  ringing: "warning",
  answered: "warning",
  initiated: "warning",
  in_progress: "warning",
  assigned: "info",
  callback_required: "warning",
  follow_up_pending: "info",
  completed: "success",
  booked: "success",
  success: "success",
  escalated: "warning",
  human_needed: "warning",
  failed: "danger",
  dropped: "danger",
  busy: "danger",
  no_answer: "danger",
  cancelled: "muted",
  not_interested: "danger",
};

const TICKET_STATUS_LABELS = {
  received: "Received",
  open: "Open",
  closed: "Closed",
};

const TICKET_STATUS_VARIANTS = {
  received: "info",
  open: "warning",
  closed: "success",
};

const TICKET_STATUS_OPTIONS = [
  { value: "received", label: "Received" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

const EMPTY_CALL_FORM = {
  phone: "",
  patient_name: "",
  reason: "",
  ai_config_id: "",
};

function normalizeStatus(status) {
  return String(status || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function humanize(value) {
  const text = String(value || "").trim();

  if (!text) {
    return "--";
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getRowId(row = {}) {
  return row.call_id || row.id || row.call_uuid || "";
}

function getCallerName(row = {}) {
  return (
    row.name ||
    row.patient_name ||
    row.caller_name ||
    row.contact_name ||
    row.contact?.name ||
    "Unknown"
  );
}

function getPhone(row = {}) {
  return row.phone_number || row.phone || row.contact?.phone || "";
}

function getSource(row = {}) {
  return (
    row.source ||
    row.metadata?.source ||
    row.metadata?.origin ||
    row.campaign_name ||
    row.service_type ||
    "Dashboard"
  );
}

function getNotes(row = {}) {
  return (
    row.notes ||
    row.latest_note ||
    row.summary_preview ||
    row.summary?.summary ||
    row.summary ||
    row.metadata?.notes ||
    row.metadata?.reason ||
    ""
  );
}

function getStatusMeta(status) {
  const normalized = normalizeStatus(status);

  return {
    label: STATUS_LABELS[normalized] || humanize(normalized),
    variant: STATUS_VARIANTS[normalized] || "info",
  };
}

function getScriptStatus(row = {}) {
  const explicit = normalizeStatus(row.script_status);
  const status = normalizeStatus(row.status || row.call_status || row.booking_status);

  if (explicit === "escalated" || explicit === "escalation") {
    return { label: "Escalated", variant: "warning" };
  }

  if (
    explicit === "human_needed" ||
    explicit === "human_required" ||
    explicit === "needs_human"
  ) {
    return { label: "Human Needed", variant: "warning" };
  }

  if (explicit === "success" || ["completed", "booked", "success"].includes(status)) {
    return { label: "Success", variant: "success" };
  }

  return { label: "Failed", variant: "danger" };
}

function getTicketStatus(row = {}) {
  const status = normalizeStatus(row.ticket_status || row.metadata?.ticket_status);

  return TICKET_STATUS_LABELS[status] ? status : "received";
}

function getTicketStatusMeta(row = {}) {
  const status = getTicketStatus(row);

  return {
    status,
    label: TICKET_STATUS_LABELS[status],
    variant: TICKET_STATUS_VARIANTS[status] || "info",
  };
}

function getAssignedAgentName(row = {}) {
  return (
    row.assigned_agent_name ||
    row.assigned_agent?.name ||
    row.agent_name ||
    row.assigned_user_name ||
    ""
  );
}

function getAssignedAt(row = {}) {
  return (
    row.assigned_at ||
    row.metadata?.assigned_at ||
    row.metadata?.ticket_assigned_at ||
    row.metadata?.ticket_status_updated_at ||
    null
  );
}

function getDirectionLabel(row = {}) {
  const direction = normalizeStatus(row.direction || row.call_direction);

  if (direction === "inbound") {
    return "Inbound";
  }

  if (direction === "outbound") {
    return "Outbound";
  }

  return "--";
}

function getHistoryDate(row = {}) {
  return row.date_time || row.ended_at || row.started_at || row.created_at || row.updated_at;
}

function getLastContacted(row = {}) {
  return (
    row.last_contacted_at ||
    row.ended_at ||
    row.answered_at ||
    row.started_at ||
    row.updated_at ||
    null
  );
}

function hasRecording(row = {}) {
  return Boolean(
    Number(row.recording_count || 0) ||
      row.recording_url ||
      row.audio_url ||
      row.recording_s3_key
  );
}

function formatPercent(value) {
  const numeric = Number(value || 0);

  if (!Number.isFinite(numeric)) {
    return "0%";
  }

  return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`;
}

function normalizeLiveCallPrefill(prefill = {}) {
  return {
    phone: prefill.phone || prefill.phone_number || "",
    patient_name:
      prefill.patient_name ||
      prefill.name ||
      prefill.caller_name ||
      prefill.contact_name ||
      "",
    reason: prefill.reason || prefill.summary || "follow-up",
    ai_config_id: prefill.ai_config_id || "",
  };
}

function validateCallForm(form) {
  const errors = {};

  if (!String(form.phone || "").trim()) {
    errors.phone = "Phone number is required.";
  }

  if (!String(form.patient_name || "").trim()) {
    errors.patient_name = "Person name is required.";
  }

  if (!String(form.reason || "").trim()) {
    errors.reason = "Reason is required.";
  }

  return errors;
}

function Pagination({ meta = {}, onPageChange }) {
  const page = Number(meta.page || 1);
  const totalPages = Number(meta.totalPages || meta.total_pages || 1);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
      <span>
        Page {page} of {totalPages}
      </span>
      <div className="flex items-center gap-2">
        <Button
          className="compact-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          className="compact-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function StatsCards({ stats = {}, loading }) {
  const cards = [
    {
      label: "Total Calls",
      value: Number(stats.totalCalls || stats.total_calls || 0),
    },
    {
      label: "Success Rate",
      value: formatPercent(stats.successRate || stats.success_rate || 0),
    },
    {
      label: "Scheduled / Queued / Pending Calls",
      value: Number(stats.scheduledQueuedPendingCalls || stats.scheduled_calls || 0),
    },
    {
      label: "In Progress Calls",
      value: Number(stats.inProgressCalls || stats.in_progress_calls || 0),
    },
    {
      label: "Dropped Calls",
      value: Number(stats.droppedCalls || stats.dropped_calls || 0),
    },
    {
      label: "Failed Calls",
      value: Number(stats.failedCalls || stats.failed_calls || 0),
    },
  ];

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
      {cards.map((card) => (
        <div
          key={card.label}
          className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="text-xs font-semibold uppercase text-slate-500">
            {card.label}
          </div>
          {loading ? (
            <Skeleton className="mt-3 h-8 w-20" />
          ) : (
            <div className="mt-2 text-2xl font-semibold text-slate-950">
              {card.value}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = getStatusMeta(status);

  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function ScriptStatusBadge({ row }) {
  const meta = getScriptStatus(row);

  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function TicketStatusControl({ row, busy, onChange }) {
  const meta = getTicketStatusMeta(row);

  return (
    <div className="flex items-center gap-2">
      <Badge variant={meta.variant}>{meta.label}</Badge>
      <select
        className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
        value={meta.status}
        disabled={busy}
        onChange={(event) => onChange(row, event.target.value)}
        aria-label="Ticket status"
      >
        {TICKET_STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function CallsPage() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showToast, registerPageControls, resetPageControls } = useShellContext();
  const mountedRef = useRef(false);
  const showToastRef = useRef(showToast);

  const [activeTab, setActiveTab] = useState(CALLS_TAB);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 350);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);

  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  const [callsData, setCallsData] = useState({ items: [], meta: {} });
  const [callsLoading, setCallsLoading] = useState(true);
  const [callsError, setCallsError] = useState("");
  const [callsPage, setCallsPage] = useState(1);

  const [historyData, setHistoryData] = useState({ items: [], meta: {} });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(1);

  const [ticketsData, setTicketsData] = useState({ items: [], meta: {} });
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");
  const [ticketsPage, setTicketsPage] = useState(1);

  const [busyAction, setBusyAction] = useState("");
  const [recordingSyncBusy, setRecordingSyncBusy] = useState(false);
  const [detailLoadingId, setDetailLoadingId] = useState("");
  const [transcriptCall, setTranscriptCall] = useState(null);

  const [assignTarget, setAssignTarget] = useState(null);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [callingAgents, setCallingAgents] = useState([]);
  const [callingAgentsLoading, setCallingAgentsLoading] = useState(false);
  const [callingAgentsError, setCallingAgentsError] = useState("");

  const [runtimeConfigs, setRuntimeConfigs] = useState([]);
  const [runtimeConfigsLoading, setRuntimeConfigsLoading] = useState(false);
  const [runtimeConfigsError, setRuntimeConfigsError] = useState("");
  const [callModal, setCallModal] = useState({
    open: false,
    form: EMPTY_CALL_FORM,
    busy: false,
    errors: {},
    submitError: "",
    successData: null,
  });

  const activeRuntimeConfigs = useMemo(
    () =>
      runtimeConfigs.filter(
        (config) => String(config.status || "").toUpperCase() === "ACTIVE"
      ),
    [runtimeConfigs]
  );

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  const loadStats = useCallback(
    async ({ silent = false, signal } = {}) => {
      if (!silent) {
        setStatsLoading(true);
      }

      try {
        const nextStats = await getCallStats(
          {
            search: debouncedSearch,
          },
          { signal }
        );

        if (!mountedRef.current) {
          return;
        }

        setStats(nextStats || {});
      } catch (error) {
        const normalized = normalizeApiError(error);

        if (!normalized.isAbort && !silent) {
          showToastRef.current(
            normalized.message || "Failed to load call stats.",
            true,
            5000
          );
        }
      } finally {
        if (mountedRef.current && !silent) {
          setStatsLoading(false);
        }
      }
    },
    [debouncedSearch]
  );

  const loadCalls = useCallback(
    async ({ silent = false, signal } = {}) => {
      if (!silent) {
        setCallsLoading(true);
      }
      setCallsError("");

      try {
        const data = await getCalls(
          {
            search: debouncedSearch,
            page: callsPage,
            limit: PAGE_SIZE,
          },
          { signal }
        );

        if (!mountedRef.current) {
          return;
        }

        setCallsData(data);
        setLastUpdated(formatNow());
      } catch (error) {
        const normalized = normalizeApiError(error);

        if (!normalized.isAbort) {
          setCallsError(normalized.message || "Failed to load calls.");
        }
      } finally {
        if (mountedRef.current && !silent) {
          setCallsLoading(false);
        }
      }
    },
    [callsPage, debouncedSearch]
  );

  const loadHistory = useCallback(
    async ({ silent = false, signal } = {}) => {
      if (!silent) {
        setHistoryLoading(true);
      }
      setHistoryError("");

      try {
        const data = await getCallHistory(
          {
            search: debouncedSearch,
            page: historyPage,
            limit: PAGE_SIZE,
          },
          { signal }
        );

        if (!mountedRef.current) {
          return;
        }

        setHistoryData(data);
        setLastUpdated(formatNow());
      } catch (error) {
        const normalized = normalizeApiError(error);

        if (!normalized.isAbort) {
          setHistoryError(normalized.message || "Failed to load call history.");
        }
      } finally {
        if (mountedRef.current && !silent) {
          setHistoryLoading(false);
        }
      }
    },
    [debouncedSearch, historyPage]
  );

  const loadTickets = useCallback(
    async ({ silent = false, signal } = {}) => {
      if (!silent) {
        setTicketsLoading(true);
      }
      setTicketsError("");

      try {
        const data = await getCallTickets(
          {
            search: debouncedSearch,
            page: ticketsPage,
            limit: PAGE_SIZE,
          },
          { signal }
        );

        if (!mountedRef.current) {
          return;
        }

        setTicketsData(data);
        setLastUpdated(formatNow());
      } catch (error) {
        const normalized = normalizeApiError(error);

        if (!normalized.isAbort) {
          setTicketsError(normalized.message || "Failed to load call tickets.");
        }
      } finally {
        if (mountedRef.current && !silent) {
          setTicketsLoading(false);
        }
      }
    },
    [debouncedSearch, ticketsPage]
  );

  const refreshCurrentView = useCallback(
    async ({ silent = false } = {}) => {
      setShellRefreshing(!silent);
      const loadActiveTab =
        activeTab === CALLS_TAB
          ? loadCalls
          : activeTab === TICKETS_TAB
            ? loadTickets
            : loadHistory;
      try {
        await Promise.all([
          loadStats({ silent }),
          loadActiveTab({ silent }),
        ]);
      } finally {
        if (mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [activeTab, loadCalls, loadHistory, loadStats, loadTickets]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setCallsPage(1);
    setHistoryPage(1);
    setTicketsPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const controller = new AbortController();

    loadStats({ signal: controller.signal });
    if (activeTab === CALLS_TAB) {
      loadCalls({ signal: controller.signal });
    } else if (activeTab === TICKETS_TAB) {
      loadTickets({ signal: controller.signal });
    } else {
      loadHistory({ signal: controller.signal });
    }

    return () => controller.abort();
  }, [activeTab, loadCalls, loadHistory, loadStats, loadTickets]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refreshCurrentView({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [refreshCurrentView]);

  useEffect(() => {
    registerPageControls({
      onRefresh: () => refreshCurrentView(),
      refreshEvery: `${Math.round(POLL_INTERVAL_MS / 1000)}s`,
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => resetPageControls();
  }, [
    lastUpdated,
    refreshCurrentView,
    registerPageControls,
    resetPageControls,
    shellRefreshing,
  ]);

  useEffect(() => {
    if (!isAdmin) {
      return undefined;
    }

    const controller = new AbortController();
    setCallingAgentsLoading(true);
    setCallingAgentsError("");

    getAssignableCallingAgents({ signal: controller.signal })
      .then((agents) => {
        if (mountedRef.current) {
          setCallingAgents(Array.isArray(agents) ? agents : []);
        }
      })
      .catch((error) => {
        const normalized = normalizeApiError(error);
        if (mountedRef.current && !normalized.isAbort) {
          setCallingAgentsError(normalized.message || "Unable to load agents.");
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setCallingAgentsLoading(false);
        }
      });

    return () => controller.abort();
  }, [isAdmin]);

  useEffect(() => {
    const controller = new AbortController();
    setRuntimeConfigsLoading(true);
    setRuntimeConfigsError("");

    getAiConfigs({ page: 1, limit: 100 }, { signal: controller.signal })
      .then((data) => {
        if (mountedRef.current) {
          setRuntimeConfigs(data.items || []);
        }
      })
      .catch((error) => {
        const normalized = normalizeApiError(error);
        if (mountedRef.current && !normalized.isAbort) {
          setRuntimeConfigsError(normalized.message || "Unable to load runtime configs.");
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setRuntimeConfigsLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  function openCallModal(prefill = {}) {
    setCallModal({
      open: true,
      form: normalizeLiveCallPrefill(prefill),
      busy: false,
      errors: {},
      submitError: "",
      successData: null,
    });
  }

  function closeCallModal() {
    if (callModal.busy) {
      return;
    }

    setCallModal((current) => ({ ...current, open: false }));
  }

  function updateCallForm(field, value) {
    setCallModal((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value,
      },
      errors: {
        ...current.errors,
        [field]: "",
      },
      submitError: "",
    }));
  }

  async function submitCallModal(event) {
    event.preventDefault();
    const errors = validateCallForm(callModal.form);

    if (Object.keys(errors).length) {
      setCallModal((current) => ({ ...current, errors }));
      return;
    }

    setCallModal((current) => ({ ...current, busy: true, submitError: "" }));

    try {
      const result = await startOutboundCall(callModal.form);
      setCallModal((current) => ({
        ...current,
        busy: false,
        successData: result,
      }));
      showToast("Call start accepted.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      setCallModal((current) => ({
        ...current,
        busy: false,
        submitError: normalized.message || "Unable to start call.",
      }));
    }
  }

  async function handleCancel(row) {
    const rowId = getRowId(row);
    if (!rowId) {
      return;
    }

    setBusyAction(`cancel-${rowId}`);
    try {
      await cancelCall(rowId, { reason: "operator_cancel" });
      showToast("Call cancelled.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to cancel call.", true, 5000);
    } finally {
      setBusyAction("");
    }
  }

  async function handleRetry(row) {
    const rowId = getRowId(row);
    if (!rowId) {
      return;
    }

    setBusyAction(`retry-${rowId}`);
    try {
      await retryCall(rowId, { reason: "manual_retry" });
      showToast("Retry scheduled.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to retry call.", true, 5000);
    } finally {
      setBusyAction("");
    }
  }

  async function handleView(row) {
    const rowId = getRowId(row);
    if (!rowId) {
      setTranscriptCall(row);
      return;
    }

    setDetailLoadingId(String(rowId));
    try {
      const detail = await getCallDetail(rowId);
      setTranscriptCall({
        ...row,
        ...detail,
        caller_name: detail.caller_name || getCallerName(row),
        phone_number: detail.phone_number || getPhone(row),
        created_at: detail.created_at || getHistoryDate(row),
        __modalRequestKey: `${rowId}-${Date.now()}`,
      });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to load call details.", true, 5000);
    } finally {
      setDetailLoadingId("");
    }
  }

  async function handleAssign(payload) {
    if (!assignTarget) {
      return;
    }

    const rowId = getRowId(assignTarget);
    setAssignmentBusy(true);

    try {
      await assignCall(rowId, payload);
      showToast("Call assigned successfully.", false, 4000);
      setAssignTarget(null);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to assign call.", true, 5000);
    } finally {
      setAssignmentBusy(false);
    }
  }

  async function handleTicketStatusChange(row, ticketStatus) {
    const rowId = getRowId(row);

    if (!rowId) {
      return;
    }

    setBusyAction(`ticket-${rowId}`);
    try {
      await updateCallTicketStatus(rowId, { status: ticketStatus });
      setTicketsData((current) => ({
        ...current,
        items: (current.items || []).map((item) =>
          String(getRowId(item)) === String(rowId)
            ? {
                ...item,
                ticket_status: ticketStatus,
                ticket_status_updated_at: new Date().toISOString(),
              }
            : item
        ),
      }));
      showToast("Ticket status updated.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to update ticket status.", true, 5000);
    } finally {
      setBusyAction("");
    }
  }

  async function handleSyncRecordings() {
    setRecordingSyncBusy(true);
    try {
      await triggerRecordingSync();
      showToast("Recording sync started.", false, 4000);
      await refreshCurrentView({ silent: true });
    } catch (error) {
      const normalized = normalizeApiError(error);
      showToast(normalized.message || "Unable to sync recordings.", true, 5000);
    } finally {
      setRecordingSyncBusy(false);
    }
  }

  const callsRows = callsData.items || [];
  const historyRows = historyData.items || [];
  const ticketsRows = ticketsData.items || [];

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-950">Calls</h1>
          <p className="mt-2 text-sm text-slate-500">
            Manage active calls and review completed call history.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin ? (
            <Button
              className="compact-btn"
              disabled={recordingSyncBusy}
              onClick={handleSyncRecordings}
            >
              <FiDownload />
              <span>{recordingSyncBusy ? "Syncing..." : "Sync Recordings"}</span>
            </Button>
          ) : null}
          <Button variant="blue" onClick={() => openCallModal()}>
            <FiPhoneCall />
            <span>Live Call</span>
          </Button>
        </div>
      </div>

      <StatsCards stats={stats} loading={statsLoading} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
          {[
            { id: CALLS_TAB, label: "Calls" },
            { id: HISTORY_TAB, label: "History" },
            { id: TICKETS_TAB, label: "Tickets" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? "bg-sky-100 text-sky-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search phone, name, or source"
            className="min-w-[260px]"
          />
          <Button
            className="compact-btn"
            disabled={shellRefreshing}
            onClick={() => refreshCurrentView()}
          >
            <FiRefreshCw />
            <span>{shellRefreshing ? "Refreshing..." : "Refresh"}</span>
          </Button>
        </div>
      </div>

      {activeTab === CALLS_TAB ? (
        <section className="space-y-4">
          {callsError ? (
            <RetryBlock message={callsError} onRetry={() => loadCalls()} />
          ) : null}

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-4 font-semibold">Name</th>
                    <th className="px-4 py-4 font-semibold">Phone Number</th>
                    <th className="px-4 py-4 font-semibold">Source</th>
                    <th className="px-4 py-4 font-semibold">Scheduled Date & Time</th>
                    <th className="px-4 py-4 font-semibold">Last Contacted</th>
                    <th className="px-4 py-4 font-semibold">Status</th>
                    <th className="px-4 py-4 font-semibold">Notes</th>
                    <th className="px-4 py-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {callsLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                      <tr key={`calls-loading-${index}`}>
                        <td className="px-4 py-4" colSpan={8}>
                          <Skeleton className="h-7 w-full" />
                        </td>
                      </tr>
                    ))
                  ) : callsRows.length ? (
                    callsRows.map((row) => {
                      const rowId = getRowId(row);
                      const status = normalizeStatus(row.status || row.call_status);
                      const canCancel = CANCELLABLE_STATUSES.has(status);
                      const canRetry = RETRYABLE_STATUSES.has(status);

                      return (
                        <tr key={rowId || row.call_uuid} className="align-top">
                          <td className="px-4 py-4 font-medium text-slate-950">
                            {getCallerName(row)}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatPhone(getPhone(row))}
                          </td>
                          <td className="px-4 py-4 text-slate-700">{getSource(row)}</td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatDateTime(row.scheduled_at || row.scheduled_time)}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatDateTime(getLastContacted(row))}
                          </td>
                          <td className="px-4 py-4">
                            <StatusBadge status={status} />
                          </td>
                          <td className="max-w-[280px] px-4 py-4 text-slate-600">
                            <span className="line-clamp-2">{getNotes(row) || "--"}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                              {canCancel ? (
                                <Button
                                  variant="danger"
                                  className="compact-btn"
                                  disabled={busyAction === `cancel-${rowId}`}
                                  onClick={() => handleCancel(row)}
                                >
                                  <FiXCircle />
                                  <span>
                                    {busyAction === `cancel-${rowId}` ? "Cancelling..." : "Cancel"}
                                  </span>
                                </Button>
                              ) : null}
                              {canRetry ? (
                                <Button
                                  className="compact-btn"
                                  disabled={busyAction === `retry-${rowId}`}
                                  onClick={() => handleRetry(row)}
                                >
                                  <FiRefreshCw />
                                  <span>
                                    {busyAction === `retry-${rowId}` ? "Retrying..." : "Retry"}
                                  </span>
                                </Button>
                              ) : null}
                              {!canCancel && !canRetry ? (
                                <span className="text-sm text-slate-400">--</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="px-4 py-12" colSpan={8}>
                        <EmptyState
                          title="No calls found"
                          subtitle="Queued, scheduled, live, pending, and in-progress calls will appear here."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination meta={callsData.meta} onPageChange={setCallsPage} />
        </section>
      ) : activeTab === HISTORY_TAB ? (
        <section className="space-y-4">
          {historyError ? (
            <RetryBlock message={historyError} onRetry={() => loadHistory()} />
          ) : null}

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-4 font-semibold">Date/Time</th>
                    <th className="px-4 py-4 font-semibold">Name</th>
                    <th className="px-4 py-4 font-semibold">Phone Number</th>
                    <th className="px-4 py-4 font-semibold">Direction</th>
                    <th className="px-4 py-4 font-semibold">Duration</th>
                    <th className="px-4 py-4 font-semibold">Script Status</th>
                    <th className="px-4 py-4 font-semibold">Audio</th>
                    <th className="px-4 py-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {historyLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                      <tr key={`history-loading-${index}`}>
                        <td className="px-4 py-4" colSpan={8}>
                          <Skeleton className="h-7 w-full" />
                        </td>
                      </tr>
                    ))
                  ) : historyRows.length ? (
                    historyRows.map((row) => {
                      const rowId = getRowId(row);

                      return (
                        <tr key={rowId || row.call_uuid} className="align-top">
                          <td className="px-4 py-4 text-slate-700">
                            {formatDateTime(getHistoryDate(row))}
                          </td>
                          <td className="px-4 py-4 font-medium text-slate-950">
                            {getCallerName(row)}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatPhone(getPhone(row))}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {getDirectionLabel(row)}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatDuration(row.duration_seconds)}
                          </td>
                          <td className="px-4 py-4">
                            <ScriptStatusBadge row={row} />
                          </td>
                          <td className="px-4 py-4">
                            {hasRecording(row) ? (
                              <RecordingButton call={row} />
                            ) : (
                              <span className="text-sm text-slate-400">No audio</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                              {isAdmin ? (
                                <Button
                                  className="compact-btn"
                                  onClick={() => setAssignTarget(row)}
                                >
                                  <FiUserCheck />
                                  <span>Assign</span>
                                </Button>
                              ) : null}
                              <Button
                                className="compact-btn"
                                disabled={detailLoadingId === String(rowId)}
                                onClick={() => handleView(row)}
                              >
                                <FiEye />
                                <span>
                                  {detailLoadingId === String(rowId) ? "Loading..." : "View"}
                                </span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="px-4 py-12" colSpan={8}>
                        <EmptyState
                          title="No history found"
                          subtitle="Completed, failed, dropped, cancelled, and past calls will appear here."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination meta={historyData.meta} onPageChange={setHistoryPage} />
        </section>
      ) : (
        <section className="space-y-4">
          {ticketsError ? (
            <RetryBlock message={ticketsError} onRetry={() => loadTickets()} />
          ) : null}

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-4 font-semibold">Date/Time</th>
                    <th className="px-4 py-4 font-semibold">Name</th>
                    <th className="px-4 py-4 font-semibold">Phone Number</th>
                    <th className="px-4 py-4 font-semibold">Direction</th>
                    <th className="px-4 py-4 font-semibold">Duration</th>
                    <th className="px-4 py-4 font-semibold">Script Status</th>
                    <th className="px-4 py-4 font-semibold">Ticket Status</th>
                    <th className="px-4 py-4 font-semibold">Agent</th>
                    <th className="px-4 py-4 font-semibold">Assigned Date & Time</th>
                    <th className="px-4 py-4 font-semibold">Audio</th>
                    <th className="px-4 py-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ticketsLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                      <tr key={`tickets-loading-${index}`}>
                        <td className="px-4 py-4" colSpan={11}>
                          <Skeleton className="h-7 w-full" />
                        </td>
                      </tr>
                    ))
                  ) : ticketsRows.length ? (
                    ticketsRows.map((row) => {
                      const rowId = getRowId(row);
                      const agentName = getAssignedAgentName(row);

                      return (
                        <tr key={rowId || row.call_uuid} className="align-top">
                          <td className="px-4 py-4 text-slate-700">
                            {formatDateTime(getHistoryDate(row))}
                          </td>
                          <td className="px-4 py-4 font-medium text-slate-950">
                            {getCallerName(row)}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatPhone(getPhone(row))}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {getDirectionLabel(row)}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatDuration(row.duration_seconds)}
                          </td>
                          <td className="px-4 py-4">
                            <ScriptStatusBadge row={row} />
                          </td>
                          <td className="px-4 py-4">
                            <TicketStatusControl
                              row={row}
                              busy={busyAction === `ticket-${rowId}`}
                              onChange={handleTicketStatusChange}
                            />
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {agentName || <span className="text-slate-400">Unassigned</span>}
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            {formatDateTime(getAssignedAt(row))}
                          </td>
                          <td className="px-4 py-4">
                            {hasRecording(row) ? (
                              <RecordingButton call={row} />
                            ) : (
                              <span className="text-sm text-slate-400">No audio</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                              {isAdmin ? (
                                <Button
                                  className="compact-btn"
                                  onClick={() => setAssignTarget(row)}
                                >
                                  <FiUserCheck />
                                  <span>Assign</span>
                                </Button>
                              ) : null}
                              <Button
                                className="compact-btn"
                                disabled={detailLoadingId === String(rowId)}
                                onClick={() => handleView(row)}
                              >
                                <FiEye />
                                <span>
                                  {detailLoadingId === String(rowId) ? "Loading..." : "View"}
                                </span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="px-4 py-12" colSpan={11}>
                        <EmptyState
                          title="No tickets found"
                          subtitle="Call history that needs tracking, escalation, or human follow-up will appear here."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination meta={ticketsData.meta} onPageChange={setTicketsPage} />
        </section>
      )}

      <StartCallModal
        open={callModal.open}
        title="Live Call"
        form={callModal.form}
        busy={callModal.busy}
        isPhoneEditable
        fieldErrors={callModal.errors}
        submitError={callModal.submitError}
        successData={callModal.successData}
        runtimeConfigs={activeRuntimeConfigs}
        runtimeConfigsLoading={runtimeConfigsLoading}
        runtimeConfigsError={runtimeConfigsError}
        onChange={updateCallForm}
        onSubmit={submitCallModal}
        onClose={closeCallModal}
      />

      <TranscriptModal
        open={Boolean(transcriptCall)}
        call={transcriptCall}
        showCallMetadata
        onDownloadTranscript={downloadTranscript}
        onClose={() => setTranscriptCall(null)}
        onCallAgain={(phone, name) => {
          setTranscriptCall(null);
          openCallModal({
            phone,
            name,
            reason: "follow-up",
          });
        }}
      />

      <AssignCallModal
        open={Boolean(assignTarget)}
        busy={assignmentBusy}
        agents={callingAgents}
        agentsLoading={callingAgentsLoading}
        agentsError={callingAgentsError}
        currentAgentId={assignTarget?.assigned_agent?.agent_id || assignTarget?.agent_id}
        callCount={1}
        onClose={() => {
          if (!assignmentBusy) {
            setAssignTarget(null);
          }
        }}
        onSubmit={handleAssign}
      />
    </div>
  );
}
